/**
 * Splits a Magic: The Gathering type line into supertype + types + subtypes
 * (TNK-120 phase A). Pure helper — no DB / network / time — so the parse is
 * unit-testable in isolation.
 *
 * Scryfall returns a single `type_line` string per face, e.g.:
 *   "Legendary Creature — Goblin Warrior"
 *   "Basic Land — Forest"
 *   "Instant"
 *   "Tribal Enchantment — Elf"
 *   "Snow Land — Forest"
 *
 * The shop's card-detail surface renders these in three separate places:
 * `tcgSupertype` (single text column on `cards`), `tcgTypes` and
 * `tcgSubtypes` (both jsonb string-arrays). This function does the parse
 * once on the server during import so we never have to re-derive at render
 * time. Lands in `cards` exactly like the Pokémon-imported equivalents.
 *
 * Supertype handling: MTG defines a fixed list of supertypes
 * (Basic / Legendary / Snow / World / Tribal / Ongoing / Host / Elite).
 * A type line can in theory carry multiple supertypes ("Legendary Snow
 * Creature — Bear") — those are joined with a single space into the one
 * text column so the operator sees "Legendary Snow" verbatim.
 *
 * Divider tolerance: Scryfall canonically uses em-dash (`—`), but cards
 * imported from older CSVs or hand-typed type lines may use hyphen-minus
 * (`-`) or double-hyphen (`--`). All three split as the same divider.
 */

/** Canonical MTG supertype vocabulary, lowercase for case-insensitive match. */
const MTG_SUPERTYPES = new Set([
  "basic",
  "legendary",
  "snow",
  "world",
  "tribal",
  "ongoing",
  "host",
  "elite",
]);

export interface MtgTypeLineParts {
  /** Joined supertype string (e.g. "Legendary", "Legendary Snow") or null
   *  when none. Casing preserved from input. */
  supertype: string | null;
  /** Card types — Creature / Instant / Sorcery / Land / Artifact /
   *  Enchantment / Planeswalker / Battle / Tribal. */
  types: string[];
  /** Subtypes after the divider (Goblin, Wizard, Forest, …). Empty for
   *  type lines with no divider. */
  subtypes: string[];
}

const EMPTY: MtgTypeLineParts = { supertype: null, types: [], subtypes: [] };

/** Splits on em-dash, en-dash, double-hyphen, or a lone hyphen surrounded
 *  by whitespace. Returns `[left]` when no divider is present. */
function splitOnDivider(line: string): string[] {
  // Order matters — the multi-char dividers must be tried first so a `--`
  // doesn't get nibbled by the single-hyphen branch.
  const dividers = [/\s+—\s+/, /\s+–\s+/, /\s+--\s+/, /\s+-\s+/];
  for (const div of dividers) {
    if (div.test(line)) return line.split(div, 2);
  }
  return [line];
}

/** Squashes runs of whitespace and strips leading/trailing space. */
function normalize(s: string): string {
  return s.replace(/\s+/g, " ").trim();
}

/**
 * Parses an MTG type line into supertype / types / subtypes.
 *
 * Returns `{ supertype: null, types: [], subtypes: [] }` for empty input
 * so callers can spread the result unconditionally during import.
 */
export function splitMtgTypeLine(
  raw: string | null | undefined,
): MtgTypeLineParts {
  if (!raw) return EMPTY;
  const line = normalize(raw);
  if (!line) return EMPTY;

  const [leftRaw, rightRaw] = splitOnDivider(line);
  const leftTokens = normalize(leftRaw).split(" ").filter(Boolean);

  const supertypeTokens: string[] = [];
  const typeTokens: string[] = [];
  for (const tok of leftTokens) {
    if (typeTokens.length === 0 && MTG_SUPERTYPES.has(tok.toLowerCase())) {
      supertypeTokens.push(tok);
    } else {
      typeTokens.push(tok);
    }
  }

  const subtypes = rightRaw
    ? normalize(rightRaw).split(" ").filter(Boolean)
    : [];

  return {
    supertype: supertypeTokens.length > 0 ? supertypeTokens.join(" ") : null,
    types: typeTokens,
    subtypes,
  };
}
